import { type ReactNode } from 'react';
import {
  AlertStatusIcon,
  CheckStatusIcon,
  EditIcon,
  InfoStatusIcon,
  LockIcon,
  TrashIcon,
  type UIIconName,
} from '@bravemove/design-tokens/icons';
import { Avatar } from '../Avatar/Avatar';
import { cn } from '../../utils/cn';

export type AuditEventKind = 'create' | 'update' | 'delete' | 'permission' | 'info' | 'alert';

export interface AuditEvent {
  readonly id: string;
  readonly at: string;
  readonly kind: AuditEventKind;
  readonly summary: ReactNode;
  readonly actor: { readonly name: string; readonly avatarSrc?: string };
  readonly details?: ReactNode;
  readonly targetLabel?: string;
}

export interface AuditLogTimelineProps {
  readonly events: readonly AuditEvent[];
  readonly className?: string;
}

const KIND_META: Record<AuditEventKind, { tint: string; icon: ReactNode }> = {
  create: {
    tint: 'bg-success/15 text-success',
    icon: <CheckStatusIcon size={12} color="currentColor" />,
  },
  update: {
    tint: 'bg-info/15 text-info',
    icon: <EditIcon size={12} color="currentColor" />,
  },
  delete: {
    tint: 'bg-danger/15 text-danger',
    icon: <TrashIcon size={12} color="currentColor" />,
  },
  permission: {
    tint: 'bg-warning/15 text-warning',
    icon: <LockIcon size={12} color="currentColor" />,
  },
  info: {
    tint: 'bg-surface-2 text-ink-2',
    icon: <InfoStatusIcon size={12} color="currentColor" />,
  },
  alert: {
    tint: 'bg-danger/15 text-danger',
    icon: <AlertStatusIcon size={12} color="currentColor" />,
  },
};

// Force-include UIIconName so the type stays exported alongside this consumer.
// Trick: a `type-only` reference, no runtime cost.
type _UnusedIconRef = UIIconName;

export function AuditLogTimeline({ events, className }: AuditLogTimelineProps) {
  return (
    <ol className={cn('relative', className)}>
      {events.map((e, i) => {
        const meta = KIND_META[e.kind];
        const isLast = i === events.length - 1;
        return (
          <li key={e.id} className="relative ps-12 pb-4 last:pb-0">
            {!isLast ? (
              <span
                aria-hidden="true"
                className="absolute start-[14px] top-7 bottom-0 w-px bg-hairline-2"
              />
            ) : null}
            <span
              className={cn(
                'absolute start-0 top-0.5 size-7 rounded-full inline-flex items-center justify-center',
                meta.tint,
              )}
            >
              {meta.icon}
            </span>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={e.actor.name} src={e.actor.avatarSrc} size="xs" />
                  <span className="text-[12.5px] font-semibold text-ink truncate">
                    {e.actor.name}
                  </span>
                  <span className="text-[12.5px] text-ink-2">{e.summary}</span>
                  {e.targetLabel ? (
                    <span className="text-[12.5px] font-mono text-accent truncate">
                      {e.targetLabel}
                    </span>
                  ) : null}
                </div>
                {e.details ? (
                  <div className="mt-1 text-[12px] text-ink-3 leading-snug">{e.details}</div>
                ) : null}
              </div>
              <span className="text-[11px] text-ink-3 font-mono tabular-nums whitespace-nowrap">
                {e.at}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
